import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOktaAuth } from '@okta/okta-react';
import OktaSignIn from '@okta/okta-signin-widget';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Typography } from '@mui/material';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';

import { oktaConfig } from './oktaConfig';
import eatonLogo from '../assets/images/Logo_White_Screen.svg';
import { ConfirmationPopUp } from '../components/ConfirmationPopUp/ConfirmationPopUp';
import { BRAND_NAME, CONTACT_EMAIL, CONTACT_PHONENO } from '../constants';
import './OktaLoginWidget.css';

/** Embedded Okta Sign-In Widget styled to match the legacy login screen. */
export const OktaLoginWidget: React.FC = () => {
    const { oktaAuth, authState } = useOktaAuth();
    const { t } = useTranslation();
    const [showContactDialog, setShowContactDialog] = React.useState(false);

    React.useEffect(() => {
        if (authState?.isAuthenticated) return;

        const { issuer, clientId, redirectUri, scopes } = oktaConfig;

        const widget = new OktaSignIn({
            baseUrl: (issuer || '').split('/oauth2')[0],
            clientId,
            redirectUri,
            logo: eatonLogo,
            brandName: BRAND_NAME,
            useInteractionCodeFlow: false,
            authParams: { issuer, scopes },
            customButtons: [
                {
                    title: t('LABELS.NEED_ACCOUNT'),
                    className: 'btn-customAuth-ac',
                    click: () => undefined,
                },
                {
                    title: t('MESSAGES.CONTACT'),
                    className: 'btn-customAuth',
                    click: () => setShowContactDialog(true),
                },
            ],
        });

        widget
            .showSignInToGetTokens({ el: '#okta-login-container' })
            .then((tokens) => oktaAuth.handleLoginRedirect(tokens))
            // eslint-disable-next-line no-console
            .catch((err) => console.error('Okta widget sign-in error', err));

        return () => widget.remove();
    }, [oktaAuth, authState?.isAuthenticated, t]);

    const contactBody = (
        <div data-testid="contact-info-dialog">
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                General Questions
            </Typography>
            <Typography variant="subtitle1" data-testid="contact-email">
                For feedback, questions or support please email us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ textDecoration: 'none' }}>
                    {CONTACT_EMAIL}
                </a>
            </Typography>
            <br />
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                Emergency Support
            </Typography>
            <Typography variant="subtitle1" data-testid="contact-phone">
                For 24/7 technical support, please call
                <br />
                <a href={`tel:${CONTACT_PHONENO}`} style={{ textDecoration: 'none' }}>
                    {CONTACT_PHONENO}
                </a>
            </Typography>
        </div>
    );

    return (
        <div className="Wrapper">
            <div id="okta-login-container" data-testid="okta-login-widget" />
            {showContactDialog && (
                <ConfirmationPopUp
                    icon={<HelpOutlineIcon style={{ fontSize: 48 }} color="primary" />}
                    body={contactBody}
                    width="xs"
                    okButtonText={t('ACTIONS.OKAY')}
                    show={showContactDialog}
                    handleOk={(): void => setShowContactDialog(false)}
                    okButtonShow={true}
                />
            )}
        </div>
    );
};
